// Third-party analysis: what outside services say about the same sample, keyed by its SHA-256, from the backend's intel
// API (GET /v1/samples/{sha256}/intel; the backend caches each provider's answer, ?force=1 asks them all again).
//   providerInfo(id) → { name, kind, submits, engines?, tags? }   display name, type and capability of a backend provider
//                       id (unknown ids as sent). submits: the backend uploads the sample there when it is not found
//                       (VirusTotal, Triage: lookup + submit + poll); the others are lookup only.
//   useThirdParty(job) → { mode, rows, fetchedAt, error, refresh, refreshing, refreshError }
//   tally(rows) → { malicious, suspicious, benign, analyzing, found, total }   for the verdict lens
//     mode: config (GET /v1/config not answered yet) · off (no intel on this backend, or no provider enabled) ·
//           loading · ready · error
//     rows: one per enabled provider (intel.providers, in the backend's order, plus any extra the answer names); a
//           provider without an answer yet reads "checking", and every row reads "error" when the lookup failed
//   While any provider is "analyzing" (the backend submitted the sample and polls that sandbox) the answer is re-read
//   every ANALYZING_POLL_MS.
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { keys, useConfig } from "@/hooks";
import { api, isApiError } from "@/lib/api";
import type { IntelResult, Job } from "@/types";

export type ThirdPartyStatus = IntelResult["status"] | "checking";
export type ThirdPartyResult = Omit<IntelResult, "status"> & { status: ThirdPartyStatus };
export type ThirdPartyMode = "config" | "off" | "loading" | "ready" | "error";

type ProviderInfo = { name: string; kind: string; submits: boolean; engines?: boolean; tags?: number };

const PROVIDER_INFO: Record<string, ProviderInfo> = {
  malwarebazaar: { name: "MalwareBazaar", kind: "intel", submits: false },
  // its tags are the YARA rules that matched, then static tags: show more of them
  yaraify: { name: "YARAify", kind: "yara", submits: false, tags: 6 },
  virustotal: { name: "VirusTotal", kind: "multi-engine", submits: true, engines: true },
  triage: { name: "Triage", kind: "sandbox", submits: true },
  hybridanalysis: { name: "Hybrid Analysis", kind: "sandbox", submits: false },
  unpacme: { name: "UnpacMe", kind: "unpacker", submits: true },
  // a corpus hit reads suspicious; never uploads (keeps private samples private)
  malshare: { name: "MalShare", kind: "corpus", submits: false },
};

export const providerInfo = (id: string): ProviderInfo => PROVIDER_INFO[id] ?? { name: id, kind: "—", submits: false };

export function tally(rows: readonly ThirdPartyResult[]) {
  const n = (f: (r: ThirdPartyResult) => boolean) => rows.filter(f).length;
  return {
    malicious: n((r) => r.status === "ok" && r.verdict === "malicious"),
    suspicious: n((r) => r.status === "ok" && r.verdict === "suspicious"),
    benign: n((r) => r.status === "ok" && r.verdict === "benign"),
    analyzing: n((r) => r.status === "analyzing"),
    found: n((r) => r.status === "ok"),
    total: rows.length,
  };
}

const SHA256 = /^[0-9a-f]{64}$/;
const ANALYZING_POLL_MS = 30_000;

const why = (e: unknown) => (isApiError(e) ? `${e.status} from ${e.path}${e.detail ? `: ${e.detail}` : ""}` : e instanceof Error ? e.message : String(e));

export function useThirdParty(job: Pick<Job, "sample_sha">) {
  const config = useConfig();
  const qc = useQueryClient();
  const sha = (job.sample_sha ?? "").trim().toLowerCase();
  const enabled = config.data?.intel?.enabled ? (config.data.intel.providers ?? []) : [];
  const on = enabled.length > 0 && SHA256.test(sha);
  const q = useQuery({
    queryKey: keys.intel(sha),
    queryFn: () => api.sampleIntel(sha),
    enabled: on,
    // the backend caches for hours; a report left open re-reads it now and then
    staleTime: 5 * 60_000,
    refetchInterval: (query) => (query.state.data?.providers.some((p) => p.status === "analyzing") ? ANALYZING_POLL_MS : false),
    retry: 1,
  });
  const refresh = useMutation({
    mutationFn: () => api.sampleIntel(sha, { force: true }),
    onSuccess: (data) => qc.setQueryData(keys.intel(sha), data),
  });

  const mode: ThirdPartyMode = !config.data && config.isPending ? "config" : !on ? "off" : q.data ? "ready" : q.isError ? "error" : "loading";
  const answers = q.data?.providers ?? [];
  const byId = new Map(answers.map((r) => [r.provider, r]));
  const ids = [...new Set([...enabled, ...answers.map((r) => r.provider)])];
  const rows: ThirdPartyResult[] = ids.map(
    (id) => byId.get(id) ?? (mode === "error" ? { provider: id, status: "error", error: why(q.error) } : { provider: id, status: "checking" }),
  );
  return {
    mode,
    rows,
    fetchedAt: q.data?.fetched_at ?? null,
    error: q.isError && !q.data ? why(q.error) : null,
    refresh: () => refresh.mutate(),
    refreshing: refresh.isPending,
    refreshError: refresh.isError ? why(refresh.error) : null,
  };
}
